import { PDFDownloadLink } from '@react-pdf/renderer';
import { Cog, ExternalLinkAlt } from '@styled-icons/fa-solid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import { Badge, OverlayTrigger, Popover, PopoverContent } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { getEpoForms, getEPOStatusesV2, updateEPOStatus } from '../../api/v2/epo';
import copyIcon from '../../images/clipart.png';
import DownloadIcon from '../../images/download.svg';
import eta from '../../images/eta.png';
import clickIcon from '../../images/Search.svg';
import UploadIcon from '../../images/Upload.svg';
import {
  confirmModalMessage,
  formatUnifiedLoadInvReference,
  showAmendmentRequiredOption,
  showUpdateStatusOption,
  statusMessages,
} from '../../utils/unifiedLoads';
import { getDDAStatus, putArrivalDate } from '../apiCalls';
import ResetButton from '../Buttons/ResetButton';
import ConfirmActionModal from '../ConfirmActionModal';
import DataTable, { useDataTableHandle } from '../DataTable';
import JobReceipt from '../JobReceipt';
import LoadCompletedModal from '../LoadCompletedModal/LoadCompletedModal';
import LoadFiltersV2 from '../LoadFiltersV2/LoadFiltersV2';
import { getDefaultCreatedDates, mapStatusOptions } from '../LoadFiltersV2/utils';
import SaveAsModal from '../UnifiedLoad/Modals/SaveAsModal';
import OverLayWrapper from '../Overlay/index';
import { useSecurity } from '../Security/security';
import { StoreContext } from '../Store';
import ToastNotification from '../Toast/ToastNotification';
import FormatDate from '../Util/FormatDate';
import DocRefModal from './DocRefModal';
import { useUnifiedEpoList } from './queryHooks/useUnifiedEpoList';
import { useUnifiedDataFileActions } from '../UnifiedLoad/queryHooks/useUnifiedDataFile';
import { UpdateEtaModal } from './UpdateEtaModal';

const epoLoadRowTestSuffix = (load) => String(load?.jobRef ?? load?.newLoadId ?? '');

const mapUnifiedEpoRow = (row) => ({
  ...row,
  route: row.routeV2 ?? row.route,
  invReference: row.invReferenceV2 ?? row.invReference,
  epoStatus: row.statusV2 ?? row.epoStatus,
});

const UnifiedEpoListLoads = () => {
  const [priorityJobs, setPriorityJobs] = useState([]);
  const [showLoadNotNewCancelModal, setShowLoadNotNewCancelModal] = useState(false);
  const [showLoadCompletedModal, setShowLoadCompletedModal] = useState(false);
  const [showUpdateEta, setShowUpdateEta] = useState(false);
  const [updateEtaRecord, setUpdateEtaRecord] = useState({});
  const [showDocRef, setShowDocRef] = useState(false);
  const { validateRole } = useSecurity();
  const [statuses, setStatuses] = useState([]);
  const [role, setRole] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [paginationLoaded, setPaginationLoaded] = useState(false);
  const [requestFailed, setRequestFailed] = useState(false);
  const store = useContext(StoreContext);
  const navigate = useNavigate();
  const dth = useDataTableHandle();
  const [dashboard, setDashboard] = useState(null);
  const [priority, setPriority] = useState(null);
  const [arrived, setArrived] = useState(null);
  const [isDanAccount, setIsdanAccount] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterData, setFilterData] = useState(false);
  const [modalConfig, setModalConfig] = useState({ show: false, type: '', load: null });
  const [openActionsLoadId, setOpenActionsLoadId] = useState(null);
  const [toastConfig, setToastConfig] = useState({ show: false, type: 'success', message: '' });
  const [openSaveAsModal, setOpenSaveAsModal] = useState(false);
  const [saveLoadAs, setSaveLoadAs] = useState(null);
  const [selectedLoadId, setSelectedLoadId] = useState(null);

  const persistedFilters = store.epoFilters[0].filters || {};
  const hasFiltersApplied = !!Object.keys(persistedFilters).length || dth?.current?.sortStatus?.currentKey;
  const { defaultCreatedFrom, defaultCreatedTo } = getDefaultCreatedDates();

  const queryClient = useQueryClient();

  const updateStatusMutation = useMutation({
    mutationFn: async ({ loadId, status }) => await updateEPOStatus(loadId, status),
    onSuccess: (_data, { status }) => {
      setToastConfig({
        show: true,
        type: 'success',
        message: statusMessages(status).success,
      });
      fetchData();
      setSaveLoadAs('');
    },
  });

  const { unifiedEpoLazyLoadMutation } = useUnifiedEpoList();
  const { downloadUnifiedDataFileMutation } = useUnifiedDataFileActions();

  useEffect(() => {
    const fetchDANData = async () => {
      const data = await getDDAStatus();
      if (data) {
        setIsdanAccount(data?.[0]?.DDA);
      }
    };
    fetchDANData();
    setRole(['LSP', 'TRADER', 'WLP'].some((dep) => validateRole({ department: dep })));
    //Reset from previous epo job
    store.eadSubmitted[1](0);
    store.sfdSubmitted[1](0);
    store.sdiSubmitted[1](0);

    // When the component mounts, set the state of dashboard, priority and arrived to the query parameters in the url. If the parameters are not present, set the state to an empty string
    const urlParams = new URLSearchParams(window.location.search);
    setDashboard(urlParams.get('dashboard') !== null ? urlParams.get('dashboard') : '');
    setPriority(urlParams.get('priority') !== null ? urlParams.get('priority') : '');
    setArrived(urlParams.get('arrived') !== null ? urlParams.get('arrived') : '');

    if (hasFiltersApplied) {
      setFilterData({ ...persistedFilters });
      setShowFilters(true);
    }

    //because we can link to unified loads we need to reset the load data
    store.resetNewLoad();
  }, []);

  useEffect(() => {
    if (paginationLoaded && dashboard !== null && priority !== null && arrived !== null) {
      //make sure that we have the values for dashboard, priority and arrived before fetching data
      fetchData();
    }
  }, [dashboard, paginationLoaded, priority, arrived]);

  const fetchData = async (newFilters, goToFirstPage = false, resetSort = false) => {
    await fetchLoads(null, newFilters, goToFirstPage, resetSort);
  };

  const updateData = async (setPaginationLength, pagination, sortStatus, search) => {
    await fetchLoads({ setPaginationLength, pagination, sortStatus, search }, null, null);
  };

  const fetchLoads = async (dataTableProps, filters, goToFirstPage, resetSort = false) => {
    try {
      const pagination = dataTableProps?.pagination || dth.current.pagination;
      const sortStatus = resetSort ? {} : dataTableProps?.sortStatus || dth.current.sortStatus;
      const paginationSetter = dataTableProps?.setPaginationLength || dth.current.setServerDataLength;

      const currentFilters = filters || persistedFilters;
      const filtersToSend = {
        createdDateFrom: defaultCreatedFrom,
        createdDateTo: defaultCreatedTo,
        excludeCompleted: 1,
        ...currentFilters,
      };

      const showCompletedEpoJobs = filtersToSend?.epoStatus?.toLowerCase() === 'completed';
      const sortStatusKey =
        showCompletedEpoJobs && sortStatus.currentKey === undefined ? 'newArrivalDate' : sortStatus.currentKey;
      const sortStatusDirection =
        showCompletedEpoJobs && sortStatus.direction === undefined ? 'desc' : sortStatus.direction;

      const payload = {
        skip: goToFirstPage ? 0 : pagination.pageNo * pagination.itemsPerPage,
        amount: pagination.itemsPerPage,
        sort_by: sortStatusKey,
        sort_order: sortStatusDirection,
        dashboardElement: dashboard,
        search: dataTableProps?.search,
        filter: filtersToSend,
        priority: priority,
        arrived: arrived,
        applyFilters: Object.keys(filtersToSend).length ? 1 : 0,
      };

      setIsLoading(true);
      const data = await unifiedEpoLazyLoadMutation.mutateAsync(payload);
      paginationSetter(data?.totalItems ?? 0);
      const rows = data?.data ?? [];
      setPriorityJobs(rows.map(mapUnifiedEpoRow));
      setStatuses(mapStatusOptions(await getEPOStatusesV2()));
      if (goToFirstPage) {
        dth.current.resetPagination();
      }
      setIsLoading(false);
    } catch (err) {
      setRequestFailed(true);
      setIsLoading(false);
    }
  };

  const fetchDocsPreference = async (id) => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ['getEpoForms', id],
        queryFn: () => getEpoForms(id),
      });
      setModalData(data);
      setShowDocRef(true);
    } catch (err) {
      setToastConfig({ show: true, type: 'error', message: 'Error fetching forms.' });
      console.error('fetch forms', err);
    }
  };

  const handleDocsRefPopup = (e, item) => {
    e.preventDefault();
    fetchDocsPreference(item.newLoadId);
  };

  function gotoDocumentUploadPage(id, transferId, isImport) {
    store.newLoadID[1](id);
    store.epoJobRef[1](transferId);
    store.epoImport[1](isImport);
    navigate(`/AdditionalEPOFileUpload/${id}`);
  }

  function gotoDownloadPage(id) {
    store.newLoadID[1](id);
    navigate('/EPODownloads');
  }

  const computeClassName = (value) => {
    switch (value) {
      case 1:
      case 2:
        return 'bg-danger text-uppercase';
      default:
        return 'bg-lightblue text-uppercase';
    }
  };

  const handleUpdateArrivalDate = async ({ date }) => {
    await putArrivalDate({
      id: updateEtaRecord.newLoadId,
      newArrivalDate: date,
    });
    updateEtaRecord.newArrivalDate = date;
  };

  const refreshTable = function () {
    return 1000 - (new Date().getTime() % 1000);
  };

  const handleReset = async () => {
    dth.current.resetPagination();
    dth.current.setSortStatus({});
    store.epoFilters[1]({});
    setFilterData({});
    await fetchData({}, true, true);
    setDashboard('');
    setPriority('');
    setArrived('');
  };

  const handleSubmit = async () => {
    store.epoFilters[1]({ filters: { ...filterData } });
    await fetchData({ ...filterData }, true);
  };

  const handleCogClick = (action, load) => {
    if (action === 'update') {
      setOpenSaveAsModal(true);
      setSelectedLoadId(parseInt(load.newLoadId));
    } else setModalConfig({ show: true, type: action, load });
  };

  const handleModalClose = () => {
    setModalConfig({ show: false, type: '', load: null });
  };

  const handleModalConfirm = async () => {
    const { type, load } = modalConfig;

    if (type === 'cancel') {
      await onUpdateStatus(load.newLoadId, 'cancelled');
    }

    if (type === 'restore') {
      await onUpdateStatus(load.newLoadId, 'restored');
    }

    if (type === 'amendment') {
      await onUpdateStatus(load.newLoadId, 'Amendment Required');
    }

    if (type === 'download') {
      try {
        await downloadUnifiedDataFileMutation.mutateAsync({
          loadId: load.newLoadId,
          fileName: load.transferId,
        });
      } catch (error) {
        console.error('Error downloading load file:', error);
      }
    }

    handleModalClose();
  };

  const onUpdateStatus = async (loadId, status) => {
    try {
      await updateStatusMutation.mutateAsync({ loadId, status });
    } catch (error) {
      console.error('Error updating load status:', error);
      setToastConfig({ show: true, type: 'error', message: statusMessages(status).error });
      setSaveLoadAs(null);
    }
  };

  const isEORIUser = () => {
    return (
      !validateRole({ department: 'TRADER' }) &&
      !validateRole({ department: 'LSP' }) &&
      !validateRole({ department: 'WLP' })
    );
  };

  const isTraderUser = validateRole({ department: 'TRADER' });
  const isWlpUser = validateRole({ department: 'WLP' });
  const isItUser = validateRole({ department: 'IT' });
  const isNonEoriUser = isTraderUser || isWlpUser;

  const showCabieDataFileInActions = (load) => load?.epoStatus !== 'New' && !isTraderUser && !isWlpUser;

  const closeActionsPopover = () => setOpenActionsLoadId(null);

  const handleCancelClick = (load) => {
    closeActionsPopover();
    if (isNonEoriUser && load?.epoStatus !== 'New') {
      setShowLoadNotNewCancelModal(true);
    } else {
      handleCogClick('cancel', load);
    }
  };

  const handleRestoreClick = (load) => {
    closeActionsPopover();
    if (isTraderUser && load?.epoStatus !== 'New') {
      setShowLoadNotNewCancelModal(true);
    } else {
      handleCogClick('restore', load);
    }
  };

  const handleDownloadClick = (load) => {
    closeActionsPopover();
    gotoDownloadPage(load.newLoadId);
  };

  const handleDataFileClick = async (load) => {
    handleCogClick('download', load);
  };

  const handleUploadClick = (load) => {
    closeActionsPopover();
    if (isTraderUser && load?.epoStatus === 'Completed') {
      setShowLoadCompletedModal(true);
    } else {
      gotoDocumentUploadPage(load.newLoadId, load.transferId, load.isImport);
    }
  };

  const getLoadStatus = (load) => load.statusV2 ?? load.epoStatus;

  if (requestFailed) return <p>Failed!</p>;

  return (
    <>
      <ToastNotification
        show={toastConfig.show}
        onClose={() => setToastConfig({ ...toastConfig, show: false })}
        type={toastConfig.type}
        message={toastConfig.message}
      />

      <UpdateEtaModal
        newArrivalDate={FormatDate(updateEtaRecord.newArrivalDate, '-', true)}
        estimatedDateOfLoading={FormatDate(updateEtaRecord.estimatedDateOfLoading, '-', true)}
        show={showUpdateEta}
        onHide={() => setShowUpdateEta(false)}
        onConfirm={handleUpdateArrivalDate}
        isImport={updateEtaRecord?.isImport}
      />
      <DocRefModal data={modalData} show={showDocRef} onHide={() => setShowDocRef(false)} />
      <div className="full-width-container">
        <section className="floating-box" style={{ maxWidth: '1530px' }}>
          <div className="d-flex justify-content-between px-3 pt-4">
            <h1 className="page-heading">{isNonEoriUser ? 'Express' : 'EPO'} Loads</h1>
            <div className="d-flex gap-4 align-items-center">
              {hasFiltersApplied && <ResetButton onClick={handleReset} inline />}
              <button
                className="stepper-button"
                type="button"
                data-testid="filters-button"
                onClick={() => setShowFilters(!showFilters)}
                style={{ minWidth: 145, maxWidth: 145 }}
                disabled={hasFiltersApplied && showFilters}
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </button>
            </div>
          </div>

          <LoadFiltersV2
            visible={showFilters}
            values={filterData}
            defaultValues={{ defaultCreatedFrom, defaultCreatedTo }}
            onChange={setFilterData}
            onSubmit={handleSubmit}
            isEORIUser={isEORIUser()}
            isEPO
            isTraderUser={isTraderUser}
          />

          <div className="px-4 pt-4">
            <div>
              <DataTable
                dontRefreshPagination
                idKey="newLoadId"
                handle={dth}
                data={priorityJobs}
                className="table-striped"
                sortable
                lazyLoading
                advancedSearch
                onUpdate={updateData}
                refreshTable={refreshTable}
                onPaginationLoaded={() => {
                  setPaginationLoaded(true);
                }}
                persistGetter={store.EPOPersist[0]}
                persistSetter={store.EPOPersist[1]}
                handleReset={handleReset}
                isLoading={isLoading}
              >
                <DataTable.Headers
                  className=" px-2"
                  style={{
                    fontSize: '12.4px',
                    backgroundColor: '#fff',
                    paddingBottom: '10px',
                    borderBottom: '1px solid #ddd',
                    borderTop: '1px solid #ddd',
                  }}
                />
                <DataTable.Pagination itemsPerPage={[10, 25, 50]} className="w-100 d-flex justify-content-end" />
                <DataTable.Row style={{ fontSize: '14px' }} />
                <DataTable.Column colKey="route" header="Route" />
                <DataTable.Column
                  colKey="transferId"
                  header="CABIE Ref"
                  searchOpts={{ type: 'text', sendAs: 'refno' }}
                />
                <DataTable.Column colKey="company" header="Trader" />
                {isEORIUser() && <DataTable.Column colKey="originator" header="Created By" />}
                <DataTable.Column colKey="invReference" header="Invoice Ref">
                  {({ invReference }) => formatUnifiedLoadInvReference(invReference)}
                </DataTable.Column>
                <DataTable.Column
                  colKey="jobRef"
                  header="Trader Ref"
                  style={{ maxWidth: '150px' }}
                  searchOpts={{ type: 'text', sendAs: 'jobReference' }}
                >
                  {({ jobRef }) => {
                    return (
                      <div className="d-flex align-items-center justify-content-between">
                        {jobRef?.length > 20 ? `${jobRef?.slice(0, 20)}` : jobRef}
                        {jobRef?.length > 20 ? <RefNoTooltip jobRef={jobRef} /> : null}
                      </div>
                    );
                  }}
                </DataTable.Column>
                <DataTable.Column colKey="newArrivalDate" header="Arrival / Exit">
                  {(item) => {
                    return (
                      <div
                        style={{ cursor: item.epoStatus === 'Completed' ? 'not-allowed' : 'pointer' }}
                        onClick={(e) => {
                          e.preventDefault();
                          setUpdateEtaRecord(item);
                          if (item.epoStatus !== 'Completed') {
                            setShowUpdateEta(true);
                          }
                        }}
                      >
                        {FormatDate(item.newArrivalDate)}
                        <img src={eta} style={{ width: '18px', height: '18px', marginLeft: '5px' }} />
                      </div>
                    );
                  }}
                </DataTable.Column>
                <DataTable.Column
                  foldable
                  colKey="submittedBy"
                  header="Created by"
                  searchOpts={{ type: 'select', src: 'data', sendAs: 'submitter' }}
                />
                <DataTable.Column
                  colKey="epoStatus"
                  header="Status"
                  searchOpts={{ type: 'select', src: statuses, sendAs: 'status' }}
                >
                  {({ epoStatus }) => {
                    return (
                      <div>
                        {epoStatus === 'Waiting for Customer' ? (
                          <div>
                            {epoStatus}
                            <span className="warning" style={{ marginLeft: '9px', height: '15px' }}></span>
                          </div>
                        ) : (
                          epoStatus
                        )}
                      </div>
                    );
                  }}
                </DataTable.Column>

                <DataTable.Column foldable colKey="lastModifiedBy" header="Updated by" />
                <DataTable.Column foldable colKey="lastModifiedDate" header="Updated">
                  {({ lastModifiedDate }) => new Date(lastModifiedDate).toLocaleString()}
                </DataTable.Column>

                {isEORIUser() && (
                  <DataTable.Column
                    colKey="Priority"
                    header="Type"
                    searchOpts={{
                      type: 'select',
                      src: [
                        { id: 0, data: 'Standard' },
                        { id: 1, data: 'Priority' },
                        { id: 2, data: 'Priority (TAD)' },
                      ],
                      sendAs: 'priority',
                    }}
                  >
                    {({ Priority }) => (
                      <Badge className={computeClassName(Priority)}>
                        {Priority === 0 ? 'Standard' : Priority === 1 ? 'Priority' : 'Priority (TAD)'}
                      </Badge>
                    )}
                  </DataTable.Column>
                )}

                <DataTable.Column
                  foldable
                  header="UPLOAD"
                  className="p-0"
                  style={{ height: '1px' }}
                  headerStyle={{ textAlign: 'center' }}
                >
                  {(load) => (
                    <button
                      className="external-table-link"
                      type="button"
                      data-testid="uploadDocuments-button"
                      disabled={
                        !validateRole({ department: 'TRADER', role: 'SUPERUSER' }) &&
                        !validateRole({ department: 'TRADER', role: 'STANDARD' }) &&
                        !validateRole({ department: 'WLP', role: 'STANDARD' }) &&
                        !validateRole({ department: 'WLP', role: 'STANDARD' }) &&
                        !validateRole({ department: 'OPS' }) &&
                        !validateRole({ department: 'QA' }) &&
                        !validateRole({ department: 'FIN' })
                      }
                      onClick={() => handleUploadClick(load)}
                    >
                      <img src={UploadIcon} width="22" height="22" />
                    </button>
                  )}
                </DataTable.Column>

                <DataTable.Column
                  foldable
                  header="DOWNLOAD"
                  className="p-0"
                  style={{ height: '1px' }}
                  headerStyle={{ textAlign: 'center' }}
                >
                  {({ newLoadId }) => (
                    <button
                      type="button"
                      className="external-table-link"
                      data-testid="downloadDocuments-button"
                      onClick={() => gotoDownloadPage(newLoadId)}
                    >
                      <img src={DownloadIcon} width="22" height="22" />
                    </button>
                  )}
                </DataTable.Column>
                {role && (
                  <DataTable.Column
                    foldable
                    header="RECEIPT"
                    className="p-0"
                    style={{ height: '1px' }}
                    headerStyle={{ textAlign: 'center' }}
                  >
                    {(epoLoad) => {
                      let newEpoLoad;
                      if (epoLoad.isImport) {
                        newEpoLoad = {
                          ...epoLoad,
                          portModal: epoLoad.GVMS === true ? 1 : 2,
                          controlledGoods: epoLoad?.confirmedNoCG === 'N' ? false : true,
                          port: epoLoad.entryport,
                          euPrefSelected: epoLoad?.exportAreaCode === 'EU' ? true : false,
                          euPref: !!epoLoad?.euPref ? 'yes' : 'no',
                        };
                      } else {
                        newEpoLoad = {
                          ...epoLoad,
                          port: epoLoad.exitport,
                          controlledGoods: epoLoad?.confirmedNoCG === 'N' ? false : true,
                          sealNo: epoLoad.SealNumber,
                          inlandTransId: epoLoad.InlandTransportID,
                          officeDest: epoLoad.officeOfDest,
                        };
                      }
                      return (
                        <PDFDownloadLink
                          document={<JobReceipt jobDetails={newEpoLoad} id="listloads" isDanAccount={isDanAccount} />}
                          fileName={'confirmation.pdf'}
                          className="invoice-add-btn green-text fw-bolder"
                          style={{ fontSize: '16px' }}
                        >
                          {
                            <button className="external-table-link">
                              <img src={DownloadIcon} width="22" height="22" />
                            </button>
                          }
                        </PDFDownloadLink>
                      );
                    }}
                  </DataTable.Column>
                )}

                <DataTable.Column
                  className="p-0"
                  style={{ height: '1px' }}
                  header="Actions"
                  headerStyle={{ textAlign: 'center' }}
                >
                  {(load) => (
                    <OverLayWrapper
                      placement="top"
                      trigger={['click']}
                      rootClose={true}
                      controlledShow={openActionsLoadId === load.newLoadId}
                      popoverContent={
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <h6 className="list-loads-cog-icon-title">{load?.['transferId']}</h6>
                          {load?.epoStatus !== 'Cancelled' && (
                            <button
                              className="list-loads-cog-icon-button"
                              style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}
                              onClick={() => handleCancelClick(load)}
                            >
                              Cancel
                            </button>
                          )}
                          {load?.epoStatus === 'Cancelled' && !isNonEoriUser && (
                            <button
                              className="list-loads-cog-icon-button"
                              style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}
                              onClick={() => handleRestoreClick(load)}
                            >
                              Restore
                            </button>
                          )}
                          <button
                            type="button"
                            className="list-loads-cog-icon-button"
                            style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}
                            data-testid="downloadDocuments-button"
                            onClick={() => handleDownloadClick(load)}
                          >
                            Download Documents
                          </button>
                          <button
                            type="button"
                            className="list-loads-cog-icon-button"
                            style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}
                            data-testid="uploadDocuments-button"
                            onClick={() => handleUploadClick(load)}
                          >
                            Upload Documents
                          </button>
                          {showCabieDataFileInActions(load) && (
                            <button
                              type="button"
                              className="list-loads-cog-icon-button"
                              style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}
                              data-testid="cabie-data-file-button"
                              onClick={() => handleDataFileClick(load)}
                            >
                              CABIE Data File
                            </button>
                          )}
                          {showUpdateStatusOption(getLoadStatus(load)) && isEORIUser() && (
                            <button
                              className="list-loads-cog-icon-button"
                              style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}
                              onClick={() => handleCogClick('update', load)}
                            >
                              Update Status
                            </button>
                          )}
                          {showAmendmentRequiredOption(getLoadStatus(load), load.sfdHasMrn) && (
                            <button
                              className="list-loads-cog-icon-button"
                              style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}
                              onClick={() => handleCogClick('amendment', load)}
                            >
                              Amendment Required
                            </button>
                          )}
                        </div>
                      }
                      popoverStyle={{ minWidth: '8rem', padding: '0.5rem' }}
                    >
                      <button
                        type="button"
                        className="external-table-link"
                        aria-label={`Actions for ${load?.transferId ?? load?.newLoadId}`}
                        data-testid={`actions-button-${epoLoadRowTestSuffix(load)}`}
                        onClick={() =>
                          setOpenActionsLoadId((prev) => (prev === load.newLoadId ? null : load.newLoadId))
                        }
                      >
                        <Cog width="1rem" height="1rem" />
                      </button>
                    </OverLayWrapper>
                  )}
                </DataTable.Column>

                {isEORIUser() && !isItUser && (
                  <DataTable.Column
                    header="View"
                    style={{ height: '1px', padding: 0 }}
                    headerStyle={{ textAlign: 'center' }}
                  >
                    {(item) => {
                      return (
                        <div className="d-flex justify-content-center align-items-center w-100 h-100">
                          <Link
                            className="external-table-link"
                            data-testid={`view-button-${epoLoadRowTestSuffix(item)}`}
                            to={`/load/${item.newLoadId}`}
                          >
                            <ExternalLinkAlt width="1rem" height="1rem" />
                          </Link>
                        </div>
                      );
                    }}
                  </DataTable.Column>
                )}
                {!isEORIUser() && (
                  <DataTable.Column
                    header="Doc Refs"
                    style={{ height: '1px', padding: 0 }}
                    headerStyle={{ textAlign: 'center' }}
                  >
                    {(item) => {
                      return (
                        <Link
                          className={
                            (item.isImport === true && item.confirmedNoCG === 'N') || item.epoStatus
                              ? 'external-table-link'
                              : 'external-table-link-disabled'
                          }
                          onClick={(e) =>
                            ((item.isImport === true && item.confirmedNoCG === 'N') || item.epoStatus) &&
                            handleDocsRefPopup(e, item)
                          }
                        >
                          <ExternalLinkAlt width="0.8rem" height="0.8rem" />
                        </Link>
                      );
                    }}
                  </DataTable.Column>
                )}
              </DataTable>
            </div>
          </div>
        </section>
      </div>

      <ConfirmActionModal
        show={modalConfig.show}
        title={modalConfig.load?.transferId || ''}
        message={confirmModalMessage(modalConfig.type)}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
      />
      <LoadCompletedModal
        show={showLoadNotNewCancelModal}
        onHide={() => setShowLoadNotNewCancelModal(false)}
        title="Unable to Cancel"
        message="This Load is no longer in a 'New' Status. Please contact our Customer Service Team to cancel this Load."
      />
      <LoadCompletedModal show={showLoadCompletedModal} onHide={() => setShowLoadCompletedModal(false)} />
      <SaveAsModal
        show={openSaveAsModal}
        onHide={() => setOpenSaveAsModal(false)}
        onSave={(loadId, status) => onUpdateStatus(loadId, status)}
        loadId={selectedLoadId}
        saveLoadAs={saveLoadAs}
        setSaveLoadAs={setSaveLoadAs}
      />
    </>
  );
};

export default UnifiedEpoListLoads;

export const RefNoTooltip = ({ jobRef }) => {
  const [isCopied, setIsCopied] = useState(false);
  const popoverWidth = `${jobRef.length * 15}px`;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(jobRef);
      setIsCopied(true);
    } catch {
      setIsCopied(false);
      console('Failed to copy!');
    }

    setTimeout(() => {
      setIsCopied(false);
    }, 100);
  };

  return (
    <OverlayTrigger
      placement="right"
      trigger={['click']}
      rootClose
      overlay={
        <Popover style={{ maxWidth: popoverWidth, padding: '6px' }}>
          <PopoverContent>
            <div className="d-flex ">
              <p className={isCopied ? 'copied-text' : ''}>{jobRef}</p>
              <div style={{ marginLeft: '13px' }}>
                <img
                  src={copyIcon}
                  alt={'Full Reference'}
                  style={{
                    cursor: 'pointer',
                    width: '19px',
                    height: '19px',
                    marginBottom: '15px',
                  }}
                  onClick={(e) => copyToClipboard()}
                />
              </div>
            </div>
          </PopoverContent>
        </Popover>
      }
    >
      <img
        src={clickIcon}
        alt={'Copy to clipboard'}
        style={{
          cursor: 'pointer',
          width: '14px',
          height: '14px',
        }}
      />
    </OverlayTrigger>
  );
};
